import _ from 'lodash';
import { DataDirection } from '../../../core/types/ui/position';
import {
  Area,
  Col,
  EntitySize,
  FilterResults,
  Freeze,
  IRange,
  Row,
} from '../../types';
import { isIndexWithinRange } from '../area/range';
import { getEntitySize } from '../viewport/viewportUtils';

// #region get min index start
const getMinIndexStart = (freeze: Freeze, filter?: FilterResults) => {
  let minStart = filter?.hasFilter
    ? filter.rowsIndexes[0] !== undefined
      ? filter.rowsIndexes[0]
      : filter.lastRow + 1
    : 0;
  let minFreeze = freeze.start;

  if (
    filter &&
    filter.hasFilter &&
    freeze.frozen &&
    freeze.start <= filter?.lastRow
  ) {
    for (let f = freeze.start; f <= freeze.end; f++) {
      if (filter?.filterIndexesMap[f] !== undefined) {
        minFreeze = f;
        break;
      }
    }
  }

  return Math.max(minStart, minFreeze);
};

const getNonFreezeMinIndex = (
  freeze: Freeze,
  frozenViewportIndexes: number[] = [],
  filter?: FilterResults
) => {
  if (filter && filter?.hasFilter) {
    if (freeze.start > filter.lastRow) return freeze.end + 1; //filter.lastRow + 1;

    const { end } = getViewportStartEnd(frozenViewportIndexes);
    if (end !== -1) {
      return calculateNextIndex(end, filter);
    }
    return (
      filter.rowsIndexes.find((number) => number > freeze.end) ||
      filter.lastRow + 1
    );
  }

  return freeze.end + 1;
};

// #endregion get min index start

// #region iterate viewport forward
export interface IterateViewportParams {
  size: number;
  index: number;
}
export interface IterataeViewportConfigs {
  useNonFreezeMin: boolean;
  frozenViewportIndexes?: number[];
}
type IterateViewportCallback = (params: IterateViewportParams) => boolean;
export const iterateViewportForward = (
  start: number,
  end: number,
  // entries: T[],
  entitySizes: EntitySize[],
  hiddenRanges: IRange[],
  cb: IterateViewportCallback,
  freeze: Freeze,
  defaultSize = 0,
  zoom = 0,
  filter?: FilterResults,
  configs: Partial<IterataeViewportConfigs> = {}
) => {
  // if(filter) {
  // console.log('filter', filter);
  // console.log('useNonFreezeMin', configs);
  // console.log(
  //   'etNonFreezeMinIndex(freeze, configs.frozenViewportIndexes, filter)',
  //   getNonFreezeMinIndex(freeze, configs.frozenViewportIndexes, filter)
  // );
  // console.log("start", start)
  // }
  let currentIndex =
    configs.useNonFreezeMin && freeze.frozen
      ? Math.max(
          start,
          getNonFreezeMinIndex(freeze, configs.frozenViewportIndexes, filter)
        )
      : Math.max(start, getMinIndexStart(freeze, filter));

  const { inRange, ...hiddenRange } = isIndexWithinRange(
    currentIndex,
    hiddenRanges
  );

  if (inRange) {
    currentIndex = hiddenRange.end + 1;
  }

  while (currentIndex <= end) {
    let nextIndex = filter
      ? calculateNextIndex(currentIndex, filter)
      : currentIndex + 1;

    const { inRange, ...hiddenRange } = isIndexWithinRange(
      currentIndex,
      hiddenRanges
    );

    if (inRange) {
      nextIndex = hiddenRange.end + 1;
      if (nextIndex === currentIndex) break;
      currentIndex = nextIndex;
      continue;
    }

    const size = getEntitySize(entitySizes, currentIndex, defaultSize, zoom);
    // const item = entries[currentIndex];
    // const size = getRowColSize(defaultSize, item, zoom);

    const breakLoop = cb({
      size,
      index: currentIndex,
    });

    if (breakLoop) break;

    if (nextIndex === currentIndex) break;

    currentIndex = nextIndex;
  }
};
// #endregion

// #region iterate viewport backward
export const iterateViewportBackward = (
  start: number,
  end: number,
  // entries: T[],
  entitySizes: EntitySize[],
  hiddenRanges: IRange[],
  cb: IterateViewportCallback,
  freeze: Freeze,
  defaultSize = 0,
  zoom = 0,
  filter?: FilterResults,
  configs: Partial<IterataeViewportConfigs> = {}
) => {
  start =
    configs.useNonFreezeMin && freeze.frozen
      ? Math.max(
          start,
          getNonFreezeMinIndex(freeze, configs.frozenViewportIndexes, filter)
        )
      : Math.max(start, getMinIndexStart(freeze, filter));
  const { inRange, ...hiddenRange } = isIndexWithinRange(start, hiddenRanges);
  if (inRange) {
    start = hiddenRange.end + 1;
  }

  let currentIndex = Math.max(end, start);

  while (currentIndex >= start) {
    let prevIndex = filter
      ? calculatePrevIndex(currentIndex, start, filter)
      : currentIndex - 1;

    const { inRange, ...hiddenRange } = isIndexWithinRange(
      currentIndex,
      hiddenRanges
    );

    if (inRange) {
      prevIndex = hiddenRange.start - 1;
      if (prevIndex === currentIndex) break;
      currentIndex = prevIndex;
      continue;
    }

    const size = getEntitySize(entitySizes, currentIndex, defaultSize, zoom);
    // const item = entries[currentIndex];
    // const size = getRowColSize(defaultSize, item, zoom);

    const breakLoop = cb({
      size,
      index: currentIndex,
    });

    if (breakLoop) break;

    if (prevIndex === currentIndex) break;

    currentIndex = prevIndex;
  }
};

const calculateNextIndex = (currentPos: number, filter: FilterResults) => {
  if (!filter.hasFilter || currentPos + 1 > filter.lastRow) {
    return currentPos + 1;
  }
  const index = filter.filterIndexesMap[currentPos];

  if (index === undefined) return filter.lastRow + 1;

  const nextValue = filter.rowsIndexes[index + 1];

  return nextValue !== undefined ? nextValue : filter.lastRow + 1;
};

const calculatePrevIndex = (
  currentPos: number,
  startIndex: number,
  filter: FilterResults
) => {
  if (!filter.hasFilter || currentPos - 1 > filter.lastRow) {
    return currentPos - 1;
  }
  if (currentPos > filter.lastRow) {
    return _.last(filter.rowsIndexes) ?? startIndex;
  }

  const index = filter.filterIndexesMap[currentPos];

  if (index === undefined) return startIndex;

  const prevValue = filter.rowsIndexes[index - 1];

  return prevValue !== undefined ? prevValue : startIndex;
};
// #endregion

// #region get unHiddenPosition
export const getUnHiddenPosition = <T extends Col | Row>(
  // entries: T[],
  entitySizes: EntitySize[],
  hiddenRanges: IRange[],
  currentPos: number,
  maxIndex: number,
  direction: DataDirection,
  freeze: Freeze,
  filterResults?: FilterResults,
  skipIndexes = 1,
  configs: Partial<IterataeViewportConfigs> = {}
  // merges: Area[] = []
) => {
  let newPosition = currentPos;
  let iterationCount = 0;
  const cb = ({ index }: IterateViewportParams) => {
    if (skipIndexes == 0 && index === currentPos) {
      return true;
    }

    if (index !== currentPos) {
      iterationCount++;

      newPosition = index;
      if (iterationCount >= skipIndexes) return true;
      else return false;
    }
    return false;
  };

  if (direction === DataDirection.Forward) {
    iterateViewportForward(
      currentPos,
      maxIndex,
      entitySizes,
      hiddenRanges,
      cb,
      freeze,
      0,
      1,
      filterResults,
      configs
    );
  } else {
    iterateViewportBackward(
      0,
      currentPos,
      entitySizes,
      hiddenRanges,
      cb,
      freeze,
      0,
      1,
      filterResults,
      configs
    );
  }

  return newPosition;
};
// #endregion

export const getViewportStartEnd = (indexes: number[]) => ({
  start: indexes[0] ?? -1,
  end: _.last(indexes) ?? -1,
});
